/**
 * Service for managing post-login redirects.
 *
 * Only paths within the app (/admin/*, /join/*) are saved. Redirects expire
 * after 5 minutes so a stale entry from an abandoned login attempt does not
 * ferry the user to an unexpected destination.
 */

const REDIRECT_KEY = 'tilly_post_login_redirect';
const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface StoredRedirect {
  path: string;
  savedAt: number;
}

/** Returns true for app-owned, non-auth paths we are safe to redirect to. */
function isAllowedPath(path: string): boolean {
  return path.startsWith('/admin') || path.startsWith('/join');
}

export const redirectService = {
  /**
   * Save the current path so we can return here after login.
   * Silently ignored for disallowed paths (/, /callback, external URLs).
   */
  save(path: string): void {
    if (!isAllowedPath(path)) return;
    try {
      const entry: StoredRedirect = { path, savedAt: Date.now() };
      localStorage.setItem(REDIRECT_KEY, JSON.stringify(entry));
    } catch (err) {
      console.warn('[redirectService] Failed to save redirect URL:', err);
    }
  },

  /**
   * Retrieve the saved path and clear it in one atomic operation.
   * Returns null if there is no entry, the entry is expired, or the path
   * is no longer considered safe.
   */
  consume(): string | null {
    try {
      const raw = localStorage.getItem(REDIRECT_KEY);
      localStorage.removeItem(REDIRECT_KEY);
      if (!raw) return null;

      const entry = JSON.parse(raw) as StoredRedirect;
      const expired = Date.now() - entry.savedAt > TTL_MS;
      if (expired || !isAllowedPath(entry.path)) return null;

      return entry.path;
    } catch {
      localStorage.removeItem(REDIRECT_KEY);
      return null;
    }
  },

  /** Imperatively clear any stored redirect (e.g., on logout). */
  clear(): void {
    try {
      localStorage.removeItem(REDIRECT_KEY);
    } catch {
      // ignore
    }
  },
};
